import { ArrowUpRight, Check, Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Section, SectionHeader } from "@/components/marketing/Section";
import { cn } from "@/lib/cn";
import { CONTACT_CHANNELS } from "@/lib/contactChannels";

// Copy-to-clipboard is the reliable path: it works with no mail client or
// social app configured, which is exactly what a raw mailto: link can't
// promise. Falls back gracefully where the Clipboard API is unavailable.
function CopyButton({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context / permissions) — the literal
      // value is already visible on the page for manual selection.
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t(copied ? "contact.copied" : "contact.copy", { label })}
      className={cn(
        "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors duration-200",
        "hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
      )}
    >
      {copied ? (
        <Check className="h-4 w-4 text-primary" aria-hidden />
      ) : (
        <Copy className="h-4 w-4" aria-hidden />
      )}
    </button>
  );
}

const channelPresentation = [
  { key: "discord", color: "#858cff", tile: "bg-[#5865f2] text-white" },
  { key: "telegram", color: "#55c5fa", tile: "bg-[#0088cc] text-white" },
  { key: "x", color: "#ffffff", tile: "border border-white/20 bg-black text-white" },
  { key: "youtube", color: "#ff6565", tile: "bg-[#ff0000] text-white" },
  {
    key: "tiktok",
    color: "#69e9e4",
    tile: "bg-black text-white [&_svg]:[filter:drop-shadow(-2px_-1px_0_#25f4ee)_drop-shadow(2px_1px_0_#fe2c55)]",
  },
  { key: "email", color: "#cdf63a", tile: "bg-primary text-primary-foreground" },
];

export function Contact() {
  const { t } = useTranslation();

  return (
    <Section className="pt-12">
      <div className="mb-10 flex flex-col gap-5 lg:mb-12">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
          {t("contact.eyebrow")}
        </p>
        <SectionHeader
          pageTitle
          title={t("contact.title")}
          subtitle={t("contact.subtitle")}
          className="max-w-3xl"
        />
      </div>

      <ul className="grid gap-4 md:grid-cols-2 lg:gap-5">
        {channelPresentation.map(({ key, color, tile }) => {
          const channel = CONTACT_CHANNELS.find((item) => item.key === key);
          if (!channel) return null;
          const { label, href, display, copyValue, Icon, external } = channel;
          return (
            <li
              key={key}
              className="group relative isolate flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-colors duration-200 hover:border-white/25 focus-within:border-white/30"
              style={{
                backgroundImage: `radial-gradient(ellipse at top right, ${color}12, transparent 70%)`,
              }}
            >
              <a
                href={href}
                {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="flex flex-1 flex-col p-6 pb-5 sm:p-7 sm:pb-5 focus-visible:outline-offset-[-4px]"
              >
                <div className="mb-5 flex items-center gap-4">
                  <span
                    className={cn(
                      "flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl shadow-lg",
                      tile,
                    )}
                  >
                    <Icon className="h-7 w-7" />
                  </span>
                  <h2 className="text-xl font-semibold tracking-tight">{label}</h2>
                  <ArrowUpRight
                    aria-hidden
                    className="ml-auto h-5 w-5 text-muted-foreground transition-transform motion-safe:group-hover:translate-x-0.5 motion-safe:group-hover:-translate-y-0.5"
                  />
                </div>
                <p className="mb-6 max-w-md text-base leading-relaxed text-muted-foreground">
                  {t(`contact.channels.${key}.description`)}
                </p>
                <span
                  className="mt-auto inline-flex items-center gap-2 text-sm font-semibold"
                  style={{ color }}
                >
                  {t(`contact.channels.${key}.action`)}
                  <ArrowUpRight aria-hidden className="h-4 w-4" />
                </span>
              </a>
              <div className="mx-6 flex items-center justify-between gap-3 border-t border-white/10 py-3 sm:mx-7">
                <span className="min-w-0 break-all font-mono text-xs leading-relaxed text-muted-foreground sm:text-sm">
                  {display}
                </span>
                <CopyButton value={copyValue} label={label} />
              </div>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
